import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const navItems = [
    { label: t('nav.whoWeAre'), path: '/who-we-are' },
    { label: t('nav.whatWeOffer'), path: '/our-offer' },
    { label: t('nav.brands'), path: '/brands' },
    { label: t('nav.inspirations'), path: '/inspirations' },
    { label: t('nav.references'), path: '/references' },
    { label: t('nav.contact'), path: '/contact' }
  ];

  // Mobile menu state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Touch gesture state for swipe-down to close
  const [touchStartY, setTouchStartY] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);

  const handleNavClick = (path) => {
    setMobileOpen(false);
    setDragOffset(0);
    setTouchStartY(null);
    setTimeout(() => navigate(path), 50);
  };

  // Touch handlers for swipe-down gesture
  const onTouchStart = (e) => {
    if (!mobileOpen) return;
    setTouchStartY(e.touches[0].clientY);
    setDragOffset(0);
  };

  const onTouchMove = (e) => {
    if (!mobileOpen || touchStartY === null) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY;
    if (diff > 0) { // Only allow downward drag
      setDragOffset(diff);
    }
  };

  const onTouchEnd = () => {
    if (!mobileOpen) return;
    const CLOSE_THRESHOLD = 80; // px to close
    if (dragOffset > CLOSE_THRESHOLD) {
      setMobileOpen(false);
    }
    // reset drag
    setDragOffset(0);
    setTouchStartY(null);
  };

  // Auto-close the sheet on route change as a safety net
  useEffect(() => {
    setMobileOpen(false);
    setDragOffset(0);
    setTouchStartY(null);
  }, [location.pathname]);

  return (
    <>
      {/* Logo - Separate positioned div */}
      <div 
        className="fixed p-4 sm:p-2 md:py-6 md:px-2 top-0 sm:-top-8 md:top-0 left-2 sm:left-4 md:left-8 z-[60] cursor-pointer hover:opacity-80 transition-opacity"
        onClick={() => handleNavClick('/')}
      >
        <img 
          src="/logo vektory_bez pozadia.png" 
          alt="SMART SANITY" 
          className="h-12 mobile:h-14 tablet:h-16 laptop:h-12 w-auto object-contain"
          style={{
            imageRendering: 'high-quality',
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <div 
          className="text-white font-light text-lg tablet:text-xl tracking-wide" 
          style={{ display: 'none' }}
        >
          SMART SANITY
        </div>
      </div>

      {/* Navbar - Compact with large gray shadow (desktop only) */}
      <nav className="bg-black fixed top-0 left-0 right-0 z-[50]">
        <div className="hidden xl:block absolute inset-0 pointer-events-none" style={{ boxShadow: '0 10px 25px -3px rgba(156, 163, 175, 0.4), 0 4px 6px -2px rgba(156, 163, 175, 0.2), 0 20px 40px -10px rgba(156, 163, 175, 0.15)' }}></div>
        <div className="flex items-center justify-end pl-1 pr-2 md:px-2 lg:px-3 tablet:px-2 laptop:px-2 ml-36 mobile:ml-40 tablet:ml-44 laptop:ml-52 h-20 sm:h-20 md:h-24">
        
        {/* Navigation Links - show only when there's enough space (1200px+) */}
        <div className="hidden xl:flex items-baseline justify-end flex-wrap gap-x-6 mr-10 mt-4">
          {navItems.map((item) => (
            <button
              key={item.label}
              onClick={() => handleNavClick(item.path)}
              className="text-gray-400 hover:text-white transition-colors text-lg xl:text-xl"
            >
              {item.label}
            </button>
          ))}
          
          {/* Language Switcher - Desktop */}
          <div className="flex items-center gap-2 ml-2">
            <button 
              onClick={() => i18n.changeLanguage('sk')}
              className={`text-lg xl:text-xl font-medium transition-colors ${
                i18n.language === 'sk' ? 'text-gray-400' : 'text-gray-500 hover:text-gray-400'
              }`}
            >
              sk
            </button>
            <span className="text-gray-500">|</span>
            <button 
              onClick={() => i18n.changeLanguage('en')}
              className={`text-lg xl:text-xl font-medium transition-colors ${
                i18n.language === 'en' ? 'text-gray-400' : 'text-gray-500 hover:text-gray-400'
              }`}
            >
              en
            </button>
          </div>
        </div>

        {/* Mobile/Tablet Menu Button - show when nav items would wrap */}
        {!mobileOpen && (
          <div className="xl:hidden flex-shrink-0 ml-2 mr-4 min-w-[40px]">
            <button
              className="text-white p-2 rounded-md border-2 border-[#595959] shadow-[4px_4px_10px_0_#595959] hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#595959]"
              aria-label="Open menu"
              aria-expanded={false}
              aria-controls="mobile-bottom-sheet"
              onClick={() => setMobileOpen(true)}
            >
              <svg className="w-9 h-9 text-[#595959]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 9h16M4 15h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div 
          className="xl:hidden fixed inset-0 z-[70] bg-black/40 backdrop-blur-[2px]"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Bottom Sheet */}
      <div 
        id="mobile-bottom-sheet"
        className={`xl:hidden fixed left-0 right-0 bottom-0 z-[90] bg-black border-t border-gray-700 transition-transform duration-300 ease-out ${
          mobileOpen ? 'translate-y-0' : 'translate-y-full'
        } h-[80vh] min-h-[500px] max-h-[90vh] rounded-t-2xl flex flex-col`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ transform: `${mobileOpen ? `translateY(${dragOffset}px)` : ''}` }}
      >
        {/* Close button inside the sheet */}
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={() => setMobileOpen(false)}
          className="absolute top-3 right-3 z-[95] p-2 rounded-md border-2 border-[#595959] text-[#595959] shadow-[4px_4px_10px_0_#595959] hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#595959]"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Drag indicator */}
        <div className="flex justify-center pt-4 pb-2">
          <div className="w-12 h-1 bg-gray-600 rounded-full"></div>
        </div>

        {/* Navigation Links */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-1">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.path)}
                className="block w-full text-left px-4 py-4 text-gray-400 text-xl font-light hover:bg-gray-800 rounded-lg transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Language switcher - always visible */}
        <div className="pt-4 pb-6 border-t border-gray-700 flex items-center justify-between bg-black shadow-[0_-6px_16px_rgba(0,0,0,0.6)] mt-auto"
             style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 24px)' }}>
          <div className="px-6 w-full">
            <span className="text-gray-400 text-sm">{i18n.language === 'sk' ? 'Jazyk' : 'Language'}</span>
            <div className="flex gap-3 mt-2">
              <button 
                onClick={() => i18n.changeLanguage('sk')}
                className={`text-lg font-medium transition-colors ${
                  i18n.language === 'sk' ? 'text-gray-400' : 'text-gray-500 hover:text-gray-400'
                }`}
              >
                sk
              </button>
              <button 
                onClick={() => i18n.changeLanguage('en')}
                className={`text-lg font-medium transition-colors ${
                  i18n.language === 'en' ? 'text-gray-400' : 'text-gray-500 hover:text-gray-400'
                }`}
              >
                en
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
    </>
  );
};

export default NavBar;
